// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';

import { type WrappedModalProps, WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNotNil } from 'helpers/isNotNil';

type Props<T> = StrongOmit<
  WrappedModalProps,
  'opened' | 'onConfirm' | 'onClose' | 'children' | 'tabs' | 'selectedTab' | 'onTabChange'
> & {
  variable: T | null | undefined;
  onConfirm: (variable: T) => Promise<void> | void;
  onClose: () => void;
  children: ReactNode | ((variable: T) => ReactNode);
};

/**
 * Used when you want to open a Modal when `variable` is set.
 *
 * The non-nil variable will be passed into `onConfirm` and `children`
 * so that you do not need to do a nullish check.
 */
export const VariableTrackingModal = <T,>({ variable, children, onConfirm, ...props }: Props<T>) => {
  const onSubmit = async () => {
    if (isNotNil(variable)) await onConfirm(variable);
  };
  return (
    <WrappedModal opened={isNotNil(variable)} {...props} withCancelButton onConfirm={onSubmit}>
      {isNotNil(variable) && (typeof children === 'function' ? children(variable) : children)}
    </WrappedModal>
  );
};
